/**
 * CompanyBrandingCard
 * -------------------
 * Settings card that lets an owner/PM upload their company logo and set a
 * display brand name. Logo is sent to /api/auth/company-branding/{user_id}
 * as a base64 data URL and reused by the backend email templates
 * (kickoff/cascade/contractor invites) and by the in-app Sidebar header.
 *
 * Props:
 *   user — required, must contain user.id
 *   onSaved? — optional callback when branding is saved (receives latest values)
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Trash2, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 5 * 1024 * 1024; // 5MB raw file size

export default function CompanyBrandingCard({ user, onSaved }) {
  const [logo, setLogo] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/company-branding/${user.id}`);
        if (cancelled) return;
        setLogo(r.data?.company_logo || null);
        setBrandName(r.data?.company_brand_name || '');
      } catch (e) {
        // 404 is fine for brand-new users — just leave empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image (PNG, JPG, or SVG).');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Logo too large — max 5MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogo(e.target.result);
      setDirty(true);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      const payload = {
        company_logo: logo, // null clears, data URL sets
        company_brand_name: brandName.trim() || null,
      };
      const r = await axios.put(`${API}/auth/company-branding/${user.id}`, payload);
      toast.success('Branding saved — your logo will appear in contractor emails.');
      setDirty(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setDirty(true);
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
      data-testid="company-branding-card"
    >
      <div className="flex items-start justify-between mb-1">
        <div>
          <h2 className="text-base font-bold text-slate-900">Company branding</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Your logo and brand name appear in contractor kickoff emails, schedule
            cascade notifications, and PDF cover pages.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-5 mt-4">
        {/* Logo preview */}
        <div className="space-y-2">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
            Logo
          </Label>
          <div
            className="w-full aspect-square bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center overflow-hidden"
            data-testid="company-logo-preview"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            ) : logo ? (
              <img
                src={logo}
                alt="Company logo"
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="text-slate-400 text-center px-2">
                <ImageIcon className="w-7 h-7 mx-auto mb-1" />
                <span className="text-[10px] font-medium uppercase tracking-wide">No logo</span>
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            data-testid="company-logo-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="flex-1 text-xs"
              data-testid="company-logo-upload-btn"
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="w-3 h-3 mr-1" />
              {logo ? 'Change' : 'Upload'}
            </Button>
            {logo && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="text-xs text-red-600 hover:text-red-700"
                data-testid="company-logo-remove-btn"
                onClick={handleRemoveLogo}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          <p className="text-[10px] text-slate-500">PNG, JPG, or SVG · 5MB max · square works best</p>
        </div>

        {/* Brand name + save */}
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
              Brand name (shown in emails)
            </Label>
            <Input
              data-testid="company-brand-name-input"
              value={brandName}
              maxLength={80}
              onChange={(e) => { setBrandName(e.target.value); setDirty(true); }}
              placeholder="e.g. Ellis Real Estate"
              className="mt-1"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Falls back to your business name if left blank.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              disabled={!dirty || saving}
              data-testid="company-branding-save-btn"
              onClick={handleSave}
            >
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Save branding
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
